import { useEffect, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { generatePassword } from "@passwordflow/crypto";
import { CalendarClock, Copy, ExternalLink, Eye, EyeOff, Fingerprint, Pencil, Plus, RefreshCw, Star, Trash2 } from "lucide-react";
import { useSecretCopy } from "../../app/clipboard.js";
import { REVEAL_TIMEOUT_MS, useBreachCheck } from "../../app/preferences.js";
import { breachChecker, formatBreachCount } from "../../lib/breach.js";
import { EXPIRY_WARNING_DAYS } from "../../lib/entries.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { describePasskey } from "../../lib/passkeys.js";
import { safeHttpUrl } from "../../lib/url.js";
import { estimateStrength } from "../../lib/strength.js";
import { Checkbox, Field, PasswordGlyphs, SecretInput, StrengthMeter, Switch, TextArea, TextInput } from "../../ui/forms.js";
import { Alert, Avatar, Badge, Button, ConfirmInline, IconButton, Segmented } from "../../ui/primitives.js";
import type { SegmentOption } from "../../ui/primitives.js";
import { PAYLOAD_LIMITS, daysUntilExpiry, emptyPayload, fieldLabels, itemTypeLabel, validatePayload } from "../../vaultPayload.js";
import type { CustomField, ItemType, PasskeyCredential, VaultPayload } from "../../vaultPayload.js";
import { TYPE_INFOS, typeInfo } from "./itemTypes.js";
import { TotpCode, TotpField } from "./Totp.js";

export interface EntryDraft extends VaultPayload {
  readonly folder: string;
  readonly favorite: boolean;
}

export function draftFrom(data: VaultPayload, folder: string, favorite: boolean): EntryDraft {
  return { ...data, folder, favorite };
}

export function emptyDraft(prefillPassword: string | undefined, type: ItemType = "login"): EntryDraft {
  return { ...emptyPayload(type), password: prefillPassword ?? "", folder: "", favorite: false };
}

/** Types that use the username/secret/url trio. */
function hasCredentials(type: ItemType): boolean {
  return type !== "note";
}

/** Types with a validity date. */
function hasExpiry(type: ItemType): boolean {
  return type === "apiKey" || type === "license";
}

function formatDay(ymd: string): string {
  return new Date(`${ymd}T00:00:00`).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const TITLE_PLACEHOLDER: Readonly<Record<ItemType, string>> = {
  login: "z. B. Hetzner Console",
  apiKey: "z. B. Stripe Live-Key",
  license: "z. B. JetBrains All Products",
  note: "z. B. Notfallkontakte Rechenzentrum"
};

const SHORT_TYPE_LABEL: Readonly<Record<ItemType, string>> = {
  login: "Login",
  apiKey: "API-Key",
  license: "Lizenz",
  note: "Notiz"
};

export function Row(props: { readonly label: string; readonly children: ReactNode; readonly action?: ReactNode }): ReactNode {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-mist-2">{props.label}</p>
        <div className="mt-1 min-w-0 text-sm text-foam">{props.children}</div>
      </div>
      {props.action !== undefined ? <div className="shrink-0 pt-2">{props.action}</div> : null}
    </div>
  );
}

/** Who the passkey signs in and where; the key itself is never shown. */
function PasskeySummary(props: { readonly passkey: PasskeyCredential }): ReactNode {
  const info = describePasskey(props.passkey);
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
      <dt className="text-mist">Website</dt>
      <dd className="min-w-0 break-all">
        <span className="inline-flex items-center gap-2">
          <Fingerprint size={14} aria-hidden="true" className="shrink-0 text-mist" />
          {info.site}
        </span>
        {info.siteName !== undefined ? <span className="text-mist"> · {info.siteName}</span> : null}
      </dd>
      <dt className="text-mist">Benutzername</dt>
      <dd className="min-w-0 break-all">
        {info.user === "" ? <span className="text-mist-2">–</span> : info.user}
        {info.displayName !== undefined ? <span className="text-mist"> · {info.displayName}</span> : null}
      </dd>
      {info.createdAt !== undefined ? (
        <>
          <dt className="text-mist">Erstellt</dt>
          <dd>{formatDateTime(info.createdAt)}</dd>
        </>
      ) : null}
      <dt className="text-mist">Kennung</dt>
      <dd className="font-mono text-xs leading-5 text-mist" title="Credential-ID, gekürzt">{info.shortId}</dd>
    </dl>
  );
}

function ExpiryNotice(props: { readonly data: VaultPayload }): ReactNode {
  const days = daysUntilExpiry(props.data, Date.now());
  if (days === undefined || days > EXPIRY_WARNING_DAYS) {
    return null;
  }
  const day = formatDay(props.data.expires);
  const text = days < 0
    ? `Abgelaufen seit ${day}.`
    : days === 0
      ? "Läuft heute ab."
      : `Läuft in ${days} ${days === 1 ? "Tag" : "Tagen"} ab (${day}).`;
  return <Alert tone={days < 0 ? "danger" : "info"}>{text}</Alert>;
}

export interface EntryViewProps {
  readonly data: VaultPayload;
  readonly folder: string;
  readonly favorite: boolean;
  readonly version: number;
  readonly updatedAt: number;
  readonly onEdit?: (() => void) | undefined;
  readonly onToggleFavorite?: (() => void) | undefined;
  readonly favoritePending?: boolean | undefined;
  /** Called after a successful copy or link open, e.g. to record recent use. */
  readonly onUsed?: (() => void) | undefined;
  readonly badge?: ReactNode;
  /** Extra warnings under the actions, e.g. a breach hit. */
  readonly notice?: ReactNode;
  readonly footer: ReactNode;
}

export function EntryView(props: EntryViewProps): ReactNode {
  const { data } = props;
  const labels = fieldLabels(data.type);
  const secretCopy = useSecretCopy();
  /** Revealed secrets: "password" or "field-<index>". Everything hides again after the timeout. */
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(new Set());
  const link = safeHttpUrl(data.url);
  const strength = estimateStrength(data.password);
  const onUsed = props.onUsed;
  const credentials = hasCredentials(data.type);
  const isLogin = data.type === "login";

  useEffect((): (() => void) | undefined => {
    if (revealed.size === 0) {
      return undefined;
    }
    const timer = window.setTimeout((): void => setRevealed(new Set()), REVEAL_TIMEOUT_MS);
    return (): void => window.clearTimeout(timer);
  }, [revealed]);

  function toggle(key: string): void {
    setRevealed((prev: ReadonlySet<string>): ReadonlySet<string> => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function copy(text: string, what: string): Promise<void> {
    if (await secretCopy(text, what)) {
      onUsed?.();
    }
  }

  const masked = (value: string): ReactNode => (
    <span className="font-mono break-all text-sm tracking-[0.12em] text-mist">{"•".repeat(Math.min(16, value.length))}</span>
  );
  const missing = <span className="text-mist-2">Nicht hinterlegt</span>;

  return (
    <div className="flex min-h-full flex-col gap-5 p-5">
      <div className="flex items-start gap-3.5">
        <Avatar label={data.title} size="lg" />
        <div className="min-w-0 flex-1">
          <h2 className="font-display truncate text-xl font-bold tracking-[-0.01em] text-foam">{data.title}</h2>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {!isLogin ? <Badge tone="neutral" icon={typeInfo(data.type).icon}>{itemTypeLabel(data.type)}</Badge> : null}
            {props.folder !== "" ? <Badge tone="neutral">{props.folder}</Badge> : null}
            {props.favorite ? <Badge tone="mint" icon={Star}>Favorit</Badge> : null}
            {props.badge}
          </div>
        </div>
        {props.onToggleFavorite !== undefined ? (
          <IconButton
            label={props.favorite ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
            icon={Star}
            className={props.favorite ? "text-flow" : undefined}
            onClick={props.onToggleFavorite}
            disabled={props.favoritePending === true}
          />
        ) : null}
      </div>

      {props.onEdit !== undefined || (credentials && data.password !== "") ? (
        <div className="flex flex-wrap gap-2">
          {props.onEdit !== undefined ? (
            <Button variant="secondary" icon={Pencil} onClick={props.onEdit}>
              Bearbeiten
            </Button>
          ) : null}
          {credentials && data.password !== "" ? (
            <Button variant="secondary" icon={Copy} onClick={(): void => void copy(data.password, `${labels.password} kopiert`)}>
              {labels.password} kopieren
            </Button>
          ) : null}
        </div>
      ) : null}

      {hasExpiry(data.type) ? <ExpiryNotice data={data} /> : null}
      {props.notice}

      <div className="rounded-lg border border-line bg-ink empty:hidden">
        {credentials && (isLogin || data.username !== "") ? (
          <Row
            label={labels.username}
            action={
              data.username !== "" ? (
                <IconButton label={`${labels.username} kopieren`} icon={Copy} size="sm" onClick={(): void => void copy(data.username, `${labels.username} kopiert`)} />
              ) : undefined
            }
          >
            {data.username !== "" ? <span className="break-all">{data.username}</span> : missing}
          </Row>
        ) : null}
        {credentials ? (
          <Row
            label={labels.password}
            action={
              data.password !== "" ? (
                <div className="flex items-center gap-1">
                  <IconButton
                    label={revealed.has("password") ? `${labels.password} verbergen` : `${labels.password} anzeigen`}
                    icon={revealed.has("password") ? EyeOff : Eye}
                    size="sm"
                    onClick={(): void => toggle("password")}
                  />
                  <IconButton label={`${labels.password} kopieren`} icon={Copy} size="sm" onClick={(): void => void copy(data.password, `${labels.password} kopiert`)} />
                </div>
              ) : undefined
            }
          >
            {data.password === "" ? (
              missing
            ) : (
              <div className="space-y-1.5">
                {revealed.has("password") ? <PasswordGlyphs value={data.password} className="text-[0.95rem]" /> : masked(data.password)}
                <p className="text-xs text-mist">
                  {isLogin ? `${strength.label} · ca. ${strength.bits} Bit · ` : ""}
                  {data.password.length} Zeichen
                  {revealed.has("password") ? " · wird automatisch verborgen" : ""}
                </p>
              </div>
            )}
          </Row>
        ) : null}
        {isLogin && data.totp.trim() !== "" ? (
          <Row label="2FA-Code">
            <TotpCode totp={data.totp} onCopy={(code: string): void => void copy(code, "2FA-Code kopiert")} />
          </Row>
        ) : null}
        {isLogin && data.passkey !== null ? (
          <Row label="Passkey">
            <PasskeySummary passkey={data.passkey} />
          </Row>
        ) : null}
        {credentials && (isLogin || data.url !== "") ? (
          <Row label={labels.url}>
            {data.url === "" ? (
              missing
            ) : link !== undefined ? (
              <a href={link} target="_blank" rel="noopener noreferrer" onClick={onUsed} className="inline-flex max-w-full items-center gap-2 break-all text-flow underline-offset-4 hover:underline">
                {data.url}
                <ExternalLink size={14} aria-hidden="true" className="shrink-0" />
                <span className="sr-only">(öffnet in neuem Tab)</span>
              </a>
            ) : (
              <span className="break-all">{data.url}</span>
            )}
          </Row>
        ) : null}
        {hasExpiry(data.type) && data.expires !== "" ? (
          <Row label="Gültig bis">
            <span className="inline-flex items-center gap-2">
              <CalendarClock size={14} aria-hidden="true" className="text-mist" />
              {formatDay(data.expires)}
            </span>
          </Row>
        ) : null}
        {data.fields.map((field: CustomField, index: number): ReactNode => {
          const key = `field-${index}`;
          return (
            <Row
              key={key}
              label={field.label}
              action={
                field.value !== "" ? (
                  <div className="flex items-center gap-1">
                    {field.secret ? (
                      <IconButton
                        label={revealed.has(key) ? `${field.label} verbergen` : `${field.label} anzeigen`}
                        icon={revealed.has(key) ? EyeOff : Eye}
                        size="sm"
                        onClick={(): void => toggle(key)}
                      />
                    ) : null}
                    <IconButton label={`${field.label} kopieren`} icon={Copy} size="sm" onClick={(): void => void copy(field.value, `${field.label} kopiert`)} />
                  </div>
                ) : undefined
              }
            >
              {field.value === "" ? missing : field.secret && !revealed.has(key) ? masked(field.value) : <span className="whitespace-pre-wrap break-all">{field.value}</span>}
            </Row>
          );
        })}
        {data.notes !== "" ? (
          <Row label="Notizen">
            <p className="whitespace-pre-wrap leading-relaxed">{data.notes}</p>
          </Row>
        ) : null}
      </div>

      <p className="text-xs text-mist-2" title={formatDateTime(props.updatedAt)}>
        Zuletzt geändert {formatRelativeTime(props.updatedAt, Date.now())} · Version {props.version}
      </p>

      <div className="mt-auto flex flex-col gap-3 border-t border-line pt-4">{props.footer}</div>
    </div>
  );
}

export interface EntryEditorProps {
  readonly mode: "new" | "edit";
  readonly initial: EntryDraft;
  readonly folders: ReadonlyArray<string>;
  readonly saving: boolean;
  readonly serverError?: string | undefined;
  readonly leading?: ReactNode;
  readonly onSubmit: (payload: VaultPayload, draft: EntryDraft) => void;
  readonly onCancel: () => void;
}

const TYPE_OPTIONS: ReadonlyArray<SegmentOption<ItemType>> = TYPE_INFOS.map((info) => ({
  value: info.type,
  label: SHORT_TYPE_LABEL[info.type],
  icon: info.icon
}));

/** What gets saved: fields the chosen type does not show are cleared, so nothing invisible stays in the item. */
function payloadFrom(draft: EntryDraft): VaultPayload {
  const credentials = hasCredentials(draft.type);
  return {
    type: draft.type,
    title: draft.title.trim(),
    username: credentials ? draft.username.trim() : "",
    password: credentials ? draft.password : "",
    url: credentials ? draft.url.trim() : "",
    notes: draft.notes,
    totp: draft.type === "login" ? draft.totp.trim() : "",
    expires: hasExpiry(draft.type) ? draft.expires : "",
    fields: draft.fields.map((f: CustomField): CustomField => ({ label: f.label.trim(), value: f.value, secret: f.secret })),
    passkey: draft.type === "login" ? draft.passkey : null
  };
}

export function EntryEditor(props: EntryEditorProps): ReactNode {
  const isNew = props.mode === "new";
  const [draft, setDraft] = useState<EntryDraft>(props.initial);
  const [errors, setErrors] = useState<string[]>([]);
  const breachCheck = useBreachCheck();
  const [checking, setChecking] = useState(false);
  /** Last breach result (count 0 = clean); it applies only while the password field still holds that password. */
  const [breach, setBreach] = useState<{ readonly password: string; readonly count: number } | undefined>(undefined);
  const breachHit = breach !== undefined && breach.password === draft.password && draft.type === "login" && breach.count > 0 ? breach.count : undefined;
  const dirty = JSON.stringify(draft) !== JSON.stringify(props.initial);
  const labels = fieldLabels(draft.type);
  const credentials = hasCredentials(draft.type);
  const dropsCredentials = !credentials && (draft.username !== "" || draft.password !== "" || draft.url !== "");
  const dropsPasskey = draft.type !== "login" && draft.passkey !== null;
  /** The saved passkey is gone from the draft; saving removes it, until then it can be undone. */
  const removingPasskey = props.initial.passkey !== null && draft.passkey === null && draft.type === "login";
  const [confirmPasskeyRemoval, setConfirmPasskeyRemoval] = useState(false);

  function patch(next: Partial<EntryDraft>): void {
    setDraft((prev: EntryDraft): EntryDraft => ({ ...prev, ...next }));
    setErrors([]);
  }

  function patchField(index: number, next: Partial<CustomField>): void {
    patch({ fields: draft.fields.map((f: CustomField, i: number): CustomField => (i === index ? { ...f, ...next } : f)) });
  }

  function submit(event?: FormEvent<HTMLFormElement>): void {
    event?.preventDefault();
    if (props.saving || checking) {
      return;
    }
    const payload = payloadFrom(draft);
    const problems = validatePayload(payload);
    if (problems.length > 0) {
      setErrors(problems);
      return;
    }
    // A new login password is checked once; saving with the warning shown keeps it anyway.
    if (!needsBreachCheck(payload.password) || breach?.password === payload.password) {
      props.onSubmit(payload, draft);
      return;
    }
    void checkThenSave(payload);
  }

  function needsBreachCheck(password: string): boolean {
    return breachCheck && draft.type === "login" && password !== "" && password !== props.initial.password;
  }

  /**
   * Warns while the entry is still being edited. Runs on blur, i.e. for a finished password only:
   * checking every keystroke would send the hash prefixes of many related partial passwords, which
   * together narrow down the real one far more than a single 5-character prefix does.
   */
  function checkPasswordEarly(password: string): void {
    if (!needsBreachCheck(password) || breach?.password === password) {
      return;
    }
    breachChecker.count(password).then(
      (count: number): void => setBreach({ password, count }),
      (): void => {
        // Advisory only; saving checks again.
      }
    );
  }

  async function checkThenSave(payload: VaultPayload): Promise<void> {
    setChecking(true);
    let count = 0;
    try {
      count = await breachChecker.count(payload.password);
    } catch {
      // The check is advisory: when the service is unreachable, saving goes ahead.
    } finally {
      setChecking(false);
    }
    if (count > 0) {
      setBreach({ password: payload.password, count });
      return;
    }
    props.onSubmit(payload, draft);
  }

  useEffect((): (() => void) | undefined => {
    if (!dirty) {
      return undefined;
    }
    function onBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return (): void => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect((): (() => void) => {
    function onKey(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        submit();
      }
    }
    window.addEventListener("keydown", onKey);
    return (): void => window.removeEventListener("keydown", onKey);
  });

  const folderField = (
    <Field id="item-folder" label="Ordner" hint="Leer lassen für „Allgemein“.">
      <TextInput id="item-folder" value={draft.folder} maxLength={64} list="folder-options" autoComplete="off" onChange={(event): void => patch({ folder: event.target.value })} />
      <datalist id="folder-options">
        {props.folders.map((folder: string) => (
          <option key={folder} value={folder} />
        ))}
      </datalist>
    </Field>
  );

  return (
    <form onSubmit={submit} className="flex min-h-full flex-col gap-5 p-5" noValidate>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold tracking-[-0.01em] text-foam">{isNew ? "Neuer Eintrag" : "Eintrag bearbeiten"}</h2>
        {dirty ? <Badge tone="mint">Ungespeichert</Badge> : null}
      </div>

      {props.leading}

      <Segmented label="Art des Eintrags" value={draft.type} options={TYPE_OPTIONS} onChange={(type: ItemType): void => patch({ type })} className="w-full" />
      {dropsPasskey ? (
        <Alert tone="info">Nur Logins können einen Passkey enthalten. Der Passkey wird beim Speichern entfernt.</Alert>
      ) : null}
      {dropsCredentials ? (
        <Alert tone="info">Eine sichere Notiz hat keine Zugangsdaten. {labels.username}, Geheimnis und Adresse werden beim Speichern entfernt.</Alert>
      ) : null}

      <Field id="item-title" label="Titel" error={errors.find((e: string) => e.startsWith("Titel")) ?? undefined}>
        <TextInput id="item-title" data-autofocus={isNew || undefined} value={draft.title} maxLength={PAYLOAD_LIMITS.title} placeholder={TITLE_PLACEHOLDER[draft.type]} onChange={(event): void => patch({ title: event.target.value })} autoComplete="off" />
      </Field>

      {credentials ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="item-username" label={labels.username}>
              <TextInput id="item-username" value={draft.username} maxLength={PAYLOAD_LIMITS.username} autoComplete="off" spellCheck={false} onChange={(event): void => patch({ username: event.target.value })} />
            </Field>
            {folderField}
          </div>

          <Field
            id="item-password"
            label={labels.password}
            action={
              draft.type === "login" ? (
                <button
                  type="button"
                  onClick={(): void => patch({ password: generatePassword({ length: 24, sets: ["lower", "upper", "digits", "symbols"] }) })}
                  className="inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-flow transition-colors hover:text-flow-bright"
                >
                  <RefreshCw size={13} aria-hidden="true" />
                  Generieren
                </button>
              ) : undefined
            }
          >
            <SecretInput
              id="item-password"
              autoComplete="new-password"
              spellCheck={false}
              value={draft.password}
              maxLength={PAYLOAD_LIMITS.password}
              revealLabel={labels.password}
              onChange={(event): void => patch({ password: event.target.value })}
              onBlur={(event): void => checkPasswordEarly(event.target.value)}
            />
            {draft.type === "login" ? <StrengthMeter password={draft.password} /> : null}
          </Field>
          {breachHit !== undefined ? (
            <Alert tone="danger">
              <p>
                <strong className="font-semibold">Dieses Passwort ist bekannt.</strong> Es taucht {formatBreachCount(breachHit)} in öffentlichen
                Datenlecks auf und steht damit in Angreifer-Wortlisten. Nimm besser ein neues.
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  icon={RefreshCw}
                  onClick={(): void => patch({ password: generatePassword({ length: 24, sets: ["lower", "upper", "digits", "symbols"] }) })}
                >
                  Neues generieren
                </Button>
                <Button size="sm" variant="ghost" onClick={(): void => submit()}>
                  Trotzdem speichern
                </Button>
              </div>
            </Alert>
          ) : null}

          <div className={hasExpiry(draft.type) ? "grid gap-4 sm:grid-cols-[1fr_11rem]" : undefined}>
            <Field id="item-url" label={labels.url}>
              <TextInput id="item-url" value={draft.url} maxLength={PAYLOAD_LIMITS.url} placeholder="https://…" inputMode="url" autoComplete="off" onChange={(event): void => patch({ url: event.target.value })} />
            </Field>
            {hasExpiry(draft.type) ? (
              <Field id="item-expires" label="Gültig bis" error={errors.find((e: string) => e.startsWith("Ablaufdatum")) ?? undefined}>
                <TextInput id="item-expires" type="date" value={draft.expires} onChange={(event): void => patch({ expires: event.target.value })} />
              </Field>
            ) : null}
          </div>

          {draft.type === "login" ? (
            <TotpField value={draft.totp} error={errors.find((e: string) => e.startsWith("Der 2FA")) ?? undefined} onChange={(totp: string): void => patch({ totp })} />
          ) : null}
          {draft.type === "login" && draft.passkey !== null ? (
            <Field id="item-passkey" label="Passkey" hint="Angelegt von einer PasswordFlow-App. Der private Schlüssel bleibt verschlüsselt und wird nie angezeigt.">
              <div className="space-y-3 rounded-lg border border-line bg-ink px-3 py-2 text-foam">
                <div className="flex items-start justify-between gap-3">
                  <PasskeySummary passkey={draft.passkey} />
                  {confirmPasskeyRemoval ? null : (
                    <IconButton label="Passkey entfernen" icon={Trash2} size="sm" onClick={(): void => setConfirmPasskeyRemoval(true)} />
                  )}
                </div>
                {confirmPasskeyRemoval ? (
                  <ConfirmInline
                    message={`Passkey für ${draft.passkey.rpId} entfernen? Nach dem Speichern kannst du dich damit nicht mehr anmelden; entferne ihn auch in den Kontoeinstellungen der Website.`}
                    confirmLabel="Passkey entfernen"
                    onCancel={(): void => setConfirmPasskeyRemoval(false)}
                    onConfirm={(): void => {
                      setConfirmPasskeyRemoval(false);
                      patch({ passkey: null });
                    }}
                  />
                ) : null}
              </div>
            </Field>
          ) : null}
          {removingPasskey ? (
            <Alert tone="info">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>Der Passkey wird beim Speichern entfernt.</span>
                <Button variant="ghost" size="sm" onClick={(): void => patch({ passkey: props.initial.passkey })}>
                  Rückgängig
                </Button>
              </div>
            </Alert>
          ) : null}
        </>
      ) : (
        folderField
      )}

      <Field id="item-notes" label="Notizen" hint="Ebenfalls verschlüsselt – nur für Berechtigte sichtbar.">
        <TextArea
          id="item-notes"
          value={draft.notes}
          maxLength={PAYLOAD_LIMITS.notes}
          rows={draft.type === "note" ? 12 : undefined}
          onChange={(event): void => patch({ notes: event.target.value })}
        />
      </Field>

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1.5 text-[0.8125rem] font-medium text-foam">Eigene Felder</legend>
        {draft.fields.map((field: CustomField, index: number): ReactNode => (
          <div key={index} className="flex flex-col gap-2 rounded-lg border border-line bg-ink p-3">
            <div className="flex items-center gap-2">
              <TextInput
                aria-label={`Bezeichnung von Feld ${index + 1}`}
                value={field.label}
                maxLength={PAYLOAD_LIMITS.fieldLabel}
                placeholder="Bezeichnung, z. B. Kundennummer"
                autoComplete="off"
                onChange={(event): void => patchField(index, { label: event.target.value })}
              />
              <IconButton
                label={`Feld ${index + 1} entfernen`}
                icon={Trash2}
                size="sm"
                onClick={(): void => patch({ fields: draft.fields.filter((_: CustomField, i: number): boolean => i !== index) })}
              />
            </div>
            {field.secret ? (
              <SecretInput
                aria-label={`Wert von Feld ${index + 1}`}
                value={field.value}
                maxLength={PAYLOAD_LIMITS.fieldValue}
                autoComplete="off"
                spellCheck={false}
                revealLabel="Wert"
                onChange={(event): void => patchField(index, { value: event.target.value })}
              />
            ) : (
              <TextInput
                aria-label={`Wert von Feld ${index + 1}`}
                value={field.value}
                maxLength={PAYLOAD_LIMITS.fieldValue}
                autoComplete="off"
                onChange={(event): void => patchField(index, { value: event.target.value })}
              />
            )}
            <Checkbox id={`item-field-secret-${index}`} checked={field.secret} onChange={(secret: boolean): void => patchField(index, { secret })}>
              Vertraulich (verdeckt anzeigen)
            </Checkbox>
          </div>
        ))}
        {draft.fields.length < PAYLOAD_LIMITS.fields ? (
          <Button
            variant="ghost"
            size="sm"
            icon={Plus}
            className="self-start"
            onClick={(): void => patch({ fields: [...draft.fields, { label: "", value: "", secret: false }] })}
          >
            Feld hinzufügen
          </Button>
        ) : null}
      </fieldset>

      <div className="rounded-lg border border-line bg-ink px-4 py-2">
        <Switch label="Als Favorit markieren" checked={draft.favorite} onChange={(checked: boolean): void => patch({ favorite: checked })} />
      </div>

      {errors.length > 0 ? <Alert tone="danger">{errors.join(" ")}</Alert> : null}
      {props.serverError !== undefined ? <Alert tone="danger">{props.serverError}</Alert> : null}

      <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-line pt-4">
        <Button type="submit" loading={props.saving || checking} disabled={!dirty && !isNew}>
          Speichern
        </Button>
        <Button variant="ghost" onClick={props.onCancel}>
          Abbrechen
        </Button>
        <span className="ml-auto hidden text-xs text-mist-2 sm:inline">Strg/⌘ + S speichert</span>
      </div>
    </form>
  );
}
